import dayjs from 'dayjs';
import { useState, useMemo, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { AssetChart } from './AssetChart';
import { useAppSelector } from 'store/hook';
import { AssetSelector } from './AssetSelector';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { guessUserTimezone } from 'utils/helpers/dateTime';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import SurvivalAnalysisOverviewHandler from 'handlers/survivalAnalysisHandlers/survivalAnalysisOverview.handler';
import { WorkOrderTimeline } from './WorkOrderTimeline';

const getInitialDates = (timezoneBasedOnDisplayMode: string) => {
  const tz = timezoneBasedOnDisplayMode || guessUserTimezone();
  const initialStartDate = dayjs().tz(tz).subtract(1, 'year').utc().toDate();
  const initialEndDate = dayjs().tz(tz).endOf('day').utc().toDate();
  return { initialStartDate, initialEndDate };
};

export const adjustDateForLocalFormatting = (
  dateObj: Date,
  userTimezone?: string
): Date => {
  const tz = userTimezone ?? guessUserTimezone();
  return dayjs(dateObj).tz(tz).startOf('day').toDate();
};

const Reliability = () => {
  const survivalAnalysisHandler = new SurvivalAnalysisOverviewHandler();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const { enableDarkTheme } = useAppTheme();
  const [isSelected, setIsSelected] = useState<string>('Rolling 1 Yr');
  const [selectedAsset, setSelectedAsset] = useState<string>('All');
  const [selectedComponent, setSelectedComponent] = useState<string>('All');
  const [assetOrder, setAssetOrder] = useState<string[] | null>(null);
  const { initialStartDate, initialEndDate } = useMemo(() => {
    return getInitialDates(timezoneBasedOnDisplayMode);
  }, [timezoneBasedOnDisplayMode]);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const {
    assets,
    workOrders,
    isLoadingReliability,
    assetsWithDownTime,
    idlingData,
  } = useAppSelector((state) => state.reliabilityReducer);
  const assetList = useAppSelector((state) => state.assetReducer.assets);

  const adjustedStartDate = adjustDateForLocalFormatting(
    startDate,
    timezoneBasedOnDisplayMode
  );
  const adjustedEndDate = adjustDateForLocalFormatting(
    endDate,
    timezoneBasedOnDisplayMode
  );

  const fetchData = async () => {
    void survivalAnalysisHandler.getSurvivalReliability(
      adjustedStartDate,
      adjustedEndDate
    );
  };

  useEffect(() => {
    if (['Rolling 1 Yr', '1M', '3M', '6M', 'All'].includes(isSelected)) {
      void fetchData();
    }
  }, [isSelected]);

  useEffect(() => {
    void fetchData();
  }, []);

  const filteredWorkOrders = useMemo(() => {
    return workOrders.filter(
      (item) => item.components_category !== 'PREVENTIVE MAINTENANCE'
    );
  }, [workOrders, selectedComponent]);

  const componentFilteredWorkOrder = useMemo(() => {
    let filtered = [...filteredWorkOrders];
    if (selectedComponent !== 'All') {
      filtered = filteredWorkOrders.filter(
        (item) => item.components_category === selectedComponent
      );
    }
    return filtered;
  }, [selectedComponent, filteredWorkOrders]);

  const handleComponentSelection = (value: string) => {
    setSelectedComponent(value);
  };

  const handleAssetSelection = (value: string) => {
    setSelectedAsset(value);
  };

  return (
    <>
      {isLoadingReliability ? (
        <Box
          sx={{
            paddingTop: '150px',
          }}
        >
          <SingleFadeLoader isFullScreen={true} />
        </Box>
      ) : (
        <Box>
          <AssetSelector
            enableDarkTheme={enableDarkTheme}
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            fetchData={fetchData}
            idlingData={idlingData}
            workOrders={filteredWorkOrders}
            downtimeData={assetsWithDownTime}
            selectedComponent={selectedComponent}
            handleComponentSelection={handleComponentSelection}
            assetList={assetList}
            handleAssetSelection={handleAssetSelection}
            selectedAsset={selectedAsset}
          />
          <Box>
            <Grid container spacing={2}>
              <Grid item lg={9} md={12} sm={12}>
                <WorkOrderTimeline
                  data={componentFilteredWorkOrder}
                  enableDarkTheme={enableDarkTheme}
                  assetList={assetList}
                  selectedComponent={selectedComponent}
                  selectedAsset={selectedAsset}
                  assetOrder={assetOrder}
                />
              </Grid>
              <Grid item lg={3} md={12} sm={12}>
                <AssetChart
                  enableDarkTheme={enableDarkTheme}
                  data={assets}
                  setSelectedAsset={setSelectedAsset}
                  selectedAsset={selectedAsset}
                  assetList={assetList}
                  workOrders={componentFilteredWorkOrder}
                  startDate={adjustedStartDate}
                  endDate={adjustedEndDate}
                  idlingData={idlingData}
                  downtimeData={assetsWithDownTime}
                  assetOrder={assetOrder}
                  setAssetOrder={setAssetOrder}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Reliability;
